/** Scale-to-zero stack wake: site-load probe + Get-rates gate. */

import { getApiBase } from "./urlHelpers";

export const NM_STACK_WAKE_EVENT = "nm-stack-wake";
export const NM_STACK_WAKE_STATE_EVENT = "nm-stack-wake-state";

const POLL_MS = 2500;
const FETCH_TIMEOUT_MS = 55_000;
/** Two greens is not enough — health can flip before emit path is stable. */
export const READY_STREAK_REQUIRED = 3;
const DEFAULT_SUPPORT_AFTER_MS = 60_000;
/**
 * Soft site-load: if the first warmup probe hasn't returned by this time,
 * assume cold and show the overlay (warm stacks answer quickly).
 */
const SOFT_SHOW_IF_SLOW_MS = 2500;

/** @type {{ ready: boolean, pending: string[], supportAfterMs: number, scaleToZero: boolean, probing: boolean, forceShow: boolean, softShow?: boolean, hasProbed?: boolean, probeReady?: boolean, generation: number, waitStartedAt?: number, confirmedAt?: number } | null} */
let lastState = null;
let generation = 0;
let loopRunning = false;
/** @type {Promise<{ ready: boolean }> | null} */
let waitPromise = null;
/** @type {((v: { ready: boolean }) => void)[]} */
let waitResolvers = [];
let interactionSeen = false;
let interactionListenersArmed = false;
let pendingInteractionReason = null;
const WAKE_INTERACTION_EVENTS = ["pointermove", "pointerdown", "touchstart", "keydown"];

export function isScaleToZeroClient() {
  if (typeof window === "undefined") return false;
  const data = window.nitromuleReactData;
  if (data?.scaleToZero === true) return true;
  if (data?.scaleToZero === false) return false;
  if (Number(data?.cloudRunMinInstances) === 0) return true;
  if (data?.isStagingSite === true) return true;
  try {
    const host = window.location.hostname.toLowerCase();
    if (host === "staging.nitromule.com" || host.startsWith("staging.")) return true;
  } catch (_) {}
  return false;
}

export function getStackWakeState() {
  return lastState;
}

function publish(state) {
  lastState = state;
  try {
    window.dispatchEvent(new CustomEvent(NM_STACK_WAKE_STATE_EVENT, { detail: state }));
  } catch (_) {}
  if (state?.ready === true) {
    const resolvers = waitResolvers.splice(0, waitResolvers.length);
    waitPromise = null;
    for (const resolve of resolvers) resolve({ ready: true });
  }
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function removeInteractionListeners() {
  if (typeof window === "undefined" || !interactionListenersArmed) return;
  for (const type of WAKE_INTERACTION_EVENTS) {
    window.removeEventListener(type, handleStackWakeInteraction, true);
  }
  interactionListenersArmed = false;
}

function browserHasPriorInteraction() {
  try {
    return navigator.userActivation?.hasBeenActive === true;
  } catch {
    return false;
  }
}

/**
 * Start a pending passive wake after a genuine browser interaction.
 * Exported for focused tests; synthetic script-dispatched events are ignored.
 */
export function handleStackWakeInteraction(event) {
  if (event?.isTrusted !== true) return;
  interactionSeen = true;
  removeInteractionListeners();
  const reason = pendingInteractionReason || "first_interaction";
  pendingInteractionReason = null;
  if (!isScaleToZeroClient()) return;
  if (lastState?.ready === true || (lastState?.probing && loopRunning)) return;
  startWake({ forceShow: false, reason });
}

function waitForStackWakeInteraction(reason) {
  pendingInteractionReason ||= reason;
  if (interactionListenersArmed || typeof window === "undefined") return;
  interactionListenersArmed = true;
  for (const type of WAKE_INTERACTION_EVENTS) {
    window.addEventListener(type, handleStackWakeInteraction, {
      capture: true,
      passive: true,
    });
  }
}

function upgradeForceShow() {
  if (!lastState || lastState.ready === true) return;
  if (lastState.forceShow) return;
  publish({ ...lastState, forceShow: true, softShow: true });
  try {
    window.dispatchEvent(
      new CustomEvent(NM_STACK_WAKE_EVENT, {
        detail: {
          reason: "force_show",
          forceShow: true,
          at: Date.now(),
          generation: lastState.generation,
        },
      })
    );
  } catch (_) {}
}

/** Show overlay only when the stack is visibly cold (not for silent warm confirmation). */
function shouldBlockShowForWake(state) {
  if (!state || state.ready === true) return false;
  if (state.forceShow) return true;
  if (state.softShow) return true;
  if (state.probeReady === false) return true;
  if (state.hasProbed === false) return false;
  return false;
}

export async function fetchStackWarmup() {
  const base = getApiBase().replace(/\/$/, "");
  const url = `${base}/nitromule/v1/internal/warmup`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(url, {
      credentials: "include",
      cache: "no-store",
      signal: controller.signal,
      headers: { Accept: "application/json", "Cache-Control": "no-cache" },
    });
    clearTimeout(timeoutId);
    if (!res.ok) {
      return { ok: false, ready: false, pending: ["nitromule"], scaleToZero: true };
    }
    return await res.json();
  } catch {
    clearTimeout(timeoutId);
    return { ok: false, ready: false, pending: ["nitromule"], scaleToZero: true };
  }
}

async function runPollLoop(gen, { forceShow }) {
  if (loopRunning) return;
  loopRunning = true;
  let streak = 0;
  let showForced = forceShow;
  let softShow = forceShow;
  let sawCold = forceShow;
  const waitStartedAt = Date.now();

  // Soft load: if first probe is slow, stack is likely cold — show overlay.
  let slowTimer = null;
  if (!forceShow) {
    slowTimer = setTimeout(() => {
      if (gen !== generation) return;
      if (!lastState || lastState.ready === true || lastState.hasProbed) return;
      softShow = true;
      publish({ ...lastState, softShow: true });
    }, SOFT_SHOW_IF_SLOW_MS);
  }

  try {
    while (gen === generation) {
      if (!isScaleToZeroClient()) {
        if (slowTimer) clearTimeout(slowTimer);
        publish({
          ready: true,
          pending: [],
          supportAfterMs: DEFAULT_SUPPORT_AFTER_MS,
          scaleToZero: false,
          probing: false,
          forceShow: false,
          softShow: false,
          hasProbed: true,
          probeReady: true,
          generation: gen,
          waitStartedAt,
          confirmedAt: Date.now(),
        });
        return;
      }

      const data = await fetchStackWarmup();
      if (slowTimer) {
        clearTimeout(slowTimer);
        slowTimer = null;
      }
      if (gen !== generation) return;

      if (data.scaleToZero === false) {
        publish({
          ready: true,
          pending: [],
          supportAfterMs: data.supportAfterMs || DEFAULT_SUPPORT_AFTER_MS,
          scaleToZero: false,
          probing: false,
          forceShow: false,
          softShow: false,
          hasProbed: true,
          probeReady: true,
          generation: gen,
          waitStartedAt,
          confirmedAt: Date.now(),
        });
        return;
      }

      // Overlay may have upgraded to forceShow while we were fetching.
      if (lastState?.forceShow) {
        showForced = true;
        sawCold = true;
        softShow = true;
      }

      const probeReady = data.ready === true;
      streak = probeReady ? streak + 1 : 0;
      const confirmed = streak >= READY_STREAK_REQUIRED;

      // Soft path: silent while all greens; show once a probe is cold (keep showing until confirmed).
      if (!showForced) {
        if (!probeReady) {
          sawCold = true;
          softShow = true;
        } else {
          softShow = sawCold;
        }
      }

      publish({
        ready: confirmed,
        pending: confirmed
          ? []
          : Array.isArray(data.pending) && data.pending.length
            ? data.pending
            : ["nitromule"],
        supportAfterMs:
          typeof data.supportAfterMs === "number" ? data.supportAfterMs : DEFAULT_SUPPORT_AFTER_MS,
        scaleToZero: true,
        probing: !confirmed,
        forceShow: showForced && !confirmed,
        softShow: (showForced || softShow) && !confirmed,
        hasProbed: true,
        generation: gen,
        waitStartedAt,
        probeReady,
        streak,
        confirmedAt: confirmed ? Date.now() : undefined,
      });

      if (confirmed) return;
      await sleep(POLL_MS);
      if (gen !== generation) return;
    }
  } finally {
    if (slowTimer) clearTimeout(slowTimer);
    // Only the active generation may clear the running flag.
    if (gen === generation) loopRunning = false;
  }
}

function startWake({ forceShow = false, reason = "manual" } = {}) {
  if (!isScaleToZeroClient()) return;
  // Avoid restarting mid-probe (streak reset / overlay flicker).
  if (loopRunning && lastState?.probing && lastState?.ready === false) {
    if (forceShow) upgradeForceShow();
    try {
      window.dispatchEvent(
        new CustomEvent(NM_STACK_WAKE_EVENT, {
          detail: {
            reason,
            forceShow: !!(forceShow || lastState.forceShow),
            at: Date.now(),
            generation: lastState.generation,
          },
        })
      );
    } catch (_) {}
    return;
  }
  generation += 1;
  const gen = generation;
  loopRunning = false;
  publish({
    ready: false,
    pending: ["nitromule"],
    supportAfterMs: DEFAULT_SUPPORT_AFTER_MS,
    scaleToZero: true,
    probing: true,
    forceShow,
    // Soft load starts silent; forceShow (Get rates) shows immediately.
    softShow: forceShow,
    hasProbed: false,
    probeReady: undefined,
    generation: gen,
    waitStartedAt: Date.now(),
  });
  try {
    window.dispatchEvent(
      new CustomEvent(NM_STACK_WAKE_EVENT, {
        detail: { reason, forceShow, at: Date.now(), generation: gen },
      })
    );
  } catch (_) {}
  void runPollLoop(gen, { forceShow });
}

function beginWaitPromise() {
  if (!waitPromise) {
    waitPromise = new Promise((resolve) => {
      waitResolvers.push(resolve);
    });
  }
  return waitPromise;
}

/**
 * Passive site-load wake. Waits for a trusted pointer, touch, or keyboard
 * interaction before probing so JS-capable crawlers do not keep the stack warm.
 */
export function ensureStackWake(reason = "manual") {
  if (typeof window === "undefined") return;
  if (!isScaleToZeroClient()) return;
  if (lastState?.ready === true && !waitPromise) return;
  if (!interactionSeen && !browserHasPriorInteraction()) {
    waitForStackWakeInteraction(reason);
    return;
  }
  interactionSeen = true;
  removeInteractionListeners();
  pendingInteractionReason = null;
  // Already probing — don't reset (avoids flicker / streak reset).
  if (lastState?.probing && lastState?.ready === false && loopRunning) {
    try {
      window.dispatchEvent(
        new CustomEvent(NM_STACK_WAKE_EVENT, {
          detail: { reason, forceShow: !!lastState.forceShow, at: Date.now(), generation: lastState.generation },
        })
      );
    } catch (_) {}
    return;
  }
  startWake({ forceShow: false, reason });
}

/**
 * Block Get rates until consecutive ready probes.
 * Same silent 3-check path as site load — overlay only if stack is actually cold.
 */
export async function waitForStackReady(reason = "wait") {
  if (typeof window === "undefined") return { ready: true };
  if (!isScaleToZeroClient()) return { ready: true };
  // Calling this path is itself the result of an explicit product action.
  interactionSeen = true;
  removeInteractionListeners();
  pendingInteractionReason = null;

  // Known-ready — one silent recheck, no overlay unless cold again.
  if (lastState?.ready === true && !lastState?.probing) {
    const data = await fetchStackWarmup();
    if (data.scaleToZero === false || data.ready === true) {
      publish({ ...lastState, confirmedAt: Date.now() });
      return { ready: true };
    }
  }

  // Join in-flight probe — wait without flashing while greens are confirming.
  if (lastState?.probing && lastState?.ready === false && loopRunning) {
    if (shouldBlockShowForWake(lastState)) {
      upgradeForceShow();
    }
    return beginWaitPromise();
  }

  startWake({ forceShow: false, reason });
  return beginWaitPromise();
}
